import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { DOCUMENT, DatePipe } from "@angular/common";
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  OnInit
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { NGXLogger } from "ngx-logger";
import { environment } from "../../../../environments/environment";
import { NewsService } from "../../../core";
import { ImageSEO } from "../../../core/models/image-seo.model";
import { ProductService } from "../../../core/services/products.service";
import { SiilaTranslationService } from "../../../core/services/siila-translation.service";
import { StaticImagesSEOService } from "../../../core/services/static-images.service";
import { MoreNews } from "../../models/more-news.model";
import { NewsSearch } from "../../models/news-search.model";
import { NewsTopics } from "../../models/news-topics.model";
import { ContentTags, StateTag } from "../../models/news.model";
import { I18nService } from "../../services";
import { AnalyticsService } from "../../services/analytics.service";
import { MetaTagService } from "../../services/meta-tag.service";
import { PrerenderReadyService } from "../../services/prerender-ready.service";
import { UrlCanonicalService } from "../../services/url-canonical.service";
import { NewsNavigateService } from "../../services/news.navigate.service";
import { PWAService } from "../../services/pwa-service";
import { InstallPromptComponent } from "../dialogs";
import { ExclusiveContentModalComponent } from "../exclusive-access/exclusive-content-modal/exclusive-content-modal.component";

/**
 * The fields every tag collection on an article shares (`productTags`,
 * `stateTags`, `propertyTypeTags`), which is all the tag-name lookup needs.
 */
interface NamedTag {
  id: number;
  name: string;
}

@Component({
  selector: "app-home-news",
  templateUrl: "./home-news.component.html",
  styleUrls: ["./home-news.component.scss"]
})
export class HomeNewsComponent
  implements OnInit, AfterViewInit, AfterViewChecked {
  currentCode: string;
  isExpanded: boolean = false;
  isMobile = false;
  news: NewsSearch;
  mostRead: MoreNews[] = [];
  topics: NewsTopics[] = [];
  pageSize = 8;
  page = 0;
  total = 0;
  tagIdFilter = null;
  email: string = "";
  keyword = "";
  tagName: string = null;
  color: string;
  /**
   * The literal segment the tag URL was entered with (`tags` or `topic`). It is
   * FUNCTIONAL — `topic` switches the search to topic mode — so canonicalization
   * preserves whichever one the visitor arrived on.
   */
  tagsSegment: string = null;
  innerWidth: number;
  browserLang: string;
  marketAnalyticsSEO: ImageSEO;
  spotTransformingSEO: ImageSEO;
  siilaAcademyBannerSEO: ImageSEO;
  loadingNews: boolean = true;
  alreadyLoaded: boolean = false;
  private prerenderMarked = false;
  private readonly prerenderStart = performance.now(); // component construction time
  show = true;
  showInstall = true;
  isTopic = false;
  bannerOod: ImageSEO;
  bannerPairs: ImageSEO;
  latamCountry = "latamBr";

  @HostListener("window:resize", ["$event"])
  onResize(event) {
    this.innerWidth = window.innerWidth;
    if (innerWidth > 900) {
      this.isMobile = false;
    } else {
      this.isMobile = true;
    }
  }

  constructor(
    private newsService: NewsService,
    private i18: I18nService,
    public breakpointObserver: BreakpointObserver,
    private route: ActivatedRoute,
    private productService: ProductService,
    private snackBar: MatSnackBar,
    private siilaTranslationService: SiilaTranslationService,
    private datePipe: DatePipe,
    private metaSrv: MetaTagService,
    private staticImgSrv: StaticImagesSEOService,
    private analyticsSrv: AnalyticsService,
    private router: Router,
    private newsNavigate: NewsNavigateService,
    private matDialog: MatDialog,
    private pwaService: PWAService,
    private ngxLogger: NGXLogger,
    @Inject(DOCUMENT) private document: Document,
    private prerenderReadyService: PrerenderReadyService,
    private urlCanonical: UrlCanonicalService
  ) {
    this.currentCode = environment.platformId;
    this.breakpointObserver
      .observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
      .subscribe(result => {
        this.isMobile = result.matches;
      });
    if (this.currentCode == "latam") {
      this.browserLang = "latam";
    } else {
      this.browserLang = this.siilaTranslationService.getCurrentLanguage();
    }
  }

  ngAfterViewInit(): void {
    const scroll = localStorage.getItem("scrollPosition");
    this.show = false;
    if (scroll) {
      const scrollPosition = Number(scroll);
      setTimeout(() => {
        localStorage.removeItem("scrollPosition");
        localStorage.removeItem("pageSize");
        window.scrollTo(0, scrollPosition);
      }, 2000);

      setTimeout(() => {
        this.show = true;
      }, 4000);
    } else {
      this.show = true;
    }
  }

  ngAfterViewChecked(): void {
    // Signal Prerender.io once the news list has actually loaded. Runs every
    // change-detection cycle, so gate on the data flag and a one-shot boolean.
    if (this.alreadyLoaded && !this.prerenderMarked) {
      this.prerenderMarked = true;
      const elapsedMs = Math.round(performance.now() - this.prerenderStart);
      this.ngxLogger.debug(
        `Prerender: ready after ${elapsedMs}ms for ${this.router.url}`
      );
      this.prerenderReadyService.signalReady();
    }
  }

  ngOnInit() {
    this.setStorageBanner();
    this.bannerOod = this.staticImgSrv.getBannerOdd();
    this.bannerPairs = this.staticImgSrv.getBannerPairs();
    if (this.currentCode !== "co") {
      this.analyticsSrv.trackBannerImpression(
        this.bannerOod,
        1,
        this.currentCode
      );
      this.analyticsSrv.trackBannerImpression(
        this.bannerPairs,
        2,
        this.currentCode
      );
    }
    this.setIcons();
    setTimeout(() => {
      this.showInstall = false;
    }, 15000);

    this.setLanguageLatam();
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        window.location.reload();
        /* if (event.url && event.url.includes("/resource")) {
          window.location.reload();
        } */
      }
    });
    this.marketAnalyticsSEO = this.staticImgSrv.getMultifamilySEO();
    this.spotTransformingSEO = this.staticImgSrv.getSpotTransformingExperienceSEO();
    this.siilaAcademyBannerSEO = this.staticImgSrv.getAcademyImgSEO();
    this.route.params.subscribe(param => {
      if (param.id) {
        this.isTopic = param.tags == "topic" ? true : false;
        this.tagIdFilter = param.id;
        this.tagName = param.type;
        this.color = param.color;
        this.tagsSegment = param.tags;
      }
      this.metaSrv.setInsightsHomeTags();
      this.getNews();
      this.getMostNews();
      this.getTopics();
    });

    setTimeout(() => {
      this.configureWistiaAutoplay();
    }, 2000);
  }

  configureWistiaAutoplay(): void {
    if (typeof (window as any).Wistia !== "undefined") {
      if (this.bannerOod?.type === "wistia" && this.bannerOod.wistiaId) {
        this.tryPlayWistiaVideo(this.bannerOod.wistiaId, 0);
      }

      if (this.bannerPairs?.type === "wistia" && this.bannerPairs.wistiaId) {
        this.tryPlayWistiaVideo(this.bannerPairs.wistiaId, 0);
      }
    } else {
      setTimeout(() => {
        this.configureWistiaAutoplay();
      }, 1000);
    }
  }

  tryPlayWistiaVideo(videoId: string, attempt: number): void {
    if (attempt >= 10) {
      return;
    }

    const wistia = (window as any).Wistia;
    const video = wistia.api(videoId);

    if (video) {
      video.mute();
      video.play();
      video.bind("end", () => {
        video.time(0);
        video.play();
      });
    } else {
      setTimeout(() => {
        this.tryPlayWistiaVideo(videoId, attempt + 1);
      }, 500);
    }
  }

  setLanguageLatam() {
    const url = this.router.url;
    if (this.currentCode == "latam" && url.includes("/es")) {
      const newUrl = url.replace("/es", "/en");
      window.location.href = newUrl;
    }
  }

  setStorageBanner() {
    const totalCycle = this.getBannerCycleLength();
    const current = Number(localStorage.getItem("bannerCount")) || 0;
    const next = (current % totalCycle) + 1;
    localStorage.setItem("bannerCount", next.toString());
  }

  private getBannerCycleLength(): number {
    const totalBanners = this.currentCode === "br" ? 7 : 6;
    return totalBanners % 2 === 0
      ? totalBanners * (totalBanners / 2)
      : totalBanners * totalBanners;
  }

  getAltTxt(text: string) {
    return this.siilaTranslationService.getTranslation(text);
  }

  getNews() {
    this.loadingNews = true;
    let regionOrigin = null;
    if (this.tagIdFilter == 20 && this.currentCode == "latam") {
      const countryBR = this.siilaTranslationService.getTranslationFromJSONFiles(
        "news.brasil"
      );
      const countryMX = this.siilaTranslationService.getTranslationFromJSONFiles(
        "news.mexico"
      );
      regionOrigin =
        this.tagName.toLowerCase() == countryBR.toLowerCase()
          ? 101
          : this.tagName.toLowerCase() == countryMX.toLowerCase()
          ? 102
          : null;
    }

    this.newsService
      .getNewsSearch(
        this.browserLang,
        this.page,
        localStorage.getItem("pageSize")
          ? Number(localStorage.getItem("pageSize"))
          : this.pageSize,
        null,
        this.tagIdFilter,
        regionOrigin,
        null,
        this.isTopic
      )
      .subscribe((resp: NewsSearch) => {
        this.news = resp;
        this.loadingNews = false;
        this.alreadyLoaded = true;
        this.total = resp.totalElements;
        this.canonicalizeTagUrl();
      });
  }

  getMostNews() {
    this.newsService.getMostNews().subscribe(resp => {
      this.mostRead = resp as MoreNews[];
    });
  }

  getTopics() {
    this.newsService.getNewsTopics().subscribe(resp => {
      this.topics = resp as NewsTopics[];
      this.canonicalizeTagUrl();
    });
  }

  /**
   * Tag pages are `resource/<tags|topic>/<tag name>/<id>/<color>`, where the name
   * is written in the reader's language (`/resource/tags/Nacional/19/2` vs
   * `/resource/tags/National/19/2/lang/en`). Only the id filters the search, so a
   * name in the wrong language — or any name at all — still renders the page, and
   * the template prints that segment as the on-screen label. This rebuilds the
   * name from the id, so the URL, the label and the content agree, and crawlers
   * get a 301 onto the one form instead of a 200 per spelling.
   *
   * Deliberately does nothing when the name cannot be resolved from data already
   * on the page: guessing a name would be worse than leaving the URL alone.
   */
  private canonicalizeTagUrl(): void {
    // `tagsSegment` is absent on the legacy `insights/tags/:id/:type/:color`
    // shape, whose segments are ordered differently; leave those untouched.
    if (!this.tagIdFilter || !this.tagsSegment || !this.color) {
      return;
    }
    // Tag 20 is the "national" tag: `getNews` derives regionOrigin by comparing
    // the URL's name against the translated country names, so rewriting it would
    // change WHICH articles the page shows. Off limits.
    if (Number(this.tagIdFilter) === 20) {
      return;
    }

    const resolvedName = this.resolveTagName();
    if (!resolvedName) {
      return;
    }

    const canonicalPath = this.siilaTranslationService.buildLocalizedPath(
      `/resource/${this.tagsSegment}/${resolvedName}/${this.tagIdFilter}/${this.color}`
    );
    const canonicalUrl = this.urlCanonical.apply(canonicalPath);
    this.metaSrv.addCanonicalTag(canonicalUrl);
    // Keep the visible label in step with the corrected URL.
    this.tagName = resolvedName;
  }

  /**
   * The tag's name in the active language, resolved from the id against data the
   * page has already loaded — the topic list in topic mode, otherwise the tags
   * carried by the filtered articles themselves. Returns null when the id is not
   * found (e.g. a tag type this page never lists), which suppresses rewriting.
   */
  private resolveTagName(): string {
    const id = Number(this.tagIdFilter);

    if (this.isTopic) {
      const topic = (this.topics || []).find(t => Number(t.id) === id);
      return topic
        ? this.siilaTranslationService.getTranslation(topic.name)
        : null;
    }

    const articles = (this.news && this.news.content) || [];
    for (const article of articles) {
      // Structural typing on purpose: `contentTags` has two competing model
      // definitions in this codebase (the search model omits `productTags`), and
      // the lookup only ever needs id + name.
      const tags = article.contentTags as {
        productTags?: NamedTag[];
        propertyTypeTags?: NamedTag[];
        stateTags?: NamedTag[];
      };
      if (!tags) {
        continue;
      }
      const match: NamedTag = [
        ...(tags.propertyTypeTags || []),
        ...(tags.stateTags || []),
        ...(tags.productTags || [])
      ].find(tag => tag && Number(tag.id) === id);
      if (match) {
        return this.siilaTranslationService.getTranslation(match.name);
      }
    }
    return null;
  }

  headerImageNews(item: string, size = false): string {
    const url = item.replace("***", size ? "1280w" : "640w");
    return `${environment.cloudfrontURL}${url}`;
  }

  seeMore() {
    this.pageSize = this.pageSize + 5;
    this.getNews();
  }

  openAcademy() {
    window.open(
      "https://mailchi.mp/siila/academy-advanced",
      this.isMobile ? "_self" : "_blank"
    );
  }

  openNews(id: number, title) {
    this.newsNavigate.openNews(
      id,
      title.urlTitle ? title.urlTitle : title.headline,
      this.isMobile,
      this.pageSize,
      this.isIOS(),
      this.browserLang
    );
  }

  openNewsSearch() {
    this.newsNavigate.openNewsSearch(this.isMobile, this.keyword);
  }

  validEmail() {
    var EMAIL_REGEX = /\S+@\S+\.\S+/;

    if (this.email.match(EMAIL_REGEX)) {
      return false;
    } else {
      return true;
    }
  }

  public sendEmail() {
    let message = this.i18.get("global.newsForm.messageEmail");
    let action = this.i18.get("global.newsForm.action");
    let errorMsj = this.i18.get("global.newsForm.errorMsj");
    this.productService.sendEmail({ email: this.email }).subscribe(
      () => {
        this.snackBar.open(message, action);
        this.email = "";
      },
      () => {
        this.snackBar.open(errorMsj, action);
      }
    );
  }

  getLanguage() {
    return this.siilaTranslationService.getCurrentLanguage();
  }

  goNewsTags(tag, color, regionOrigin?: number) {
    this.newsNavigate.goNewsTags(
      tag,
      color,
      this.getNationalStateName(tag, regionOrigin),
      this.isMobile
    );
  }

  formatDate(date, format) {
    return this.datePipe.transform(date, format);
  }

  isEnglishLang() {
    return this.i18.getCurrentLanguage() === "en";
  }

  openSpot() {
    if (this.isBR()) {
      var url = this.isEnglishLang()
        ? "https://spot.siila.com.br/company/zolver/34161272/en"
        : "https://spot.siila.com.br/empresa/zolver/34161272/pt-br";
      window.open(url, this.isMobile ? "_self" : "_blank");
    } else {
      window.open(
        this.i18.buildLocalizedPath("/SPOT"),
        this.isMobile ? "_self" : "_blank"
      );
    }
  }

  openProduct() {
    window.open(
      this.i18.buildLocalizedPath("/marketanalytics"),
      this.isMobile ? "_self" : "_blank"
    );
  }

  openMultifamilyBanner() {
    let url = this.isEnglishLang()
      ? "https://mailchi.mp/siila.com.br/7gms21uue1"
      : "https://mailchi.mp/siila.com.br/multifamily";
    window.open(url, this.isMobile ? "_self" : "_blank");
  }

  goHome(latam = false) {
    this.newsNavigate.goHome(latam);
  }

  getStateTagName(contentTags: ContentTags) {
    const stateTag = contentTags.stateTags[0];
    return this.getNationalStateName(stateTag, contentTags.regionOrigin);
  }

  private getNationalStateName(stateTag: StateTag, regionOrigin: number) {
    let stateName = null;
    if (stateTag.id == 20 && regionOrigin) {
      const country = regionOrigin == 102 ? "mexico" : "brasil";
      stateName = this.siilaTranslationService
        .getTranslationFromJSONFiles("news." + country)
        .toLowerCase();
    } else {
      stateName = this.siilaTranslationService.getTranslation(stateTag.name);
    }

    return stateName;
  }

  isIOS(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  }

  validateTitle(title) {
    if (title.match(/´/)) {
      return title.replace(/´/g, "'");
    } else {
      return title;
    }
  }

  installApp() {
    if (this.isIOS()) {
      this.matDialog.open(InstallPromptComponent);
    } else {
      this.pwaService.promptPWAInstall();
    }
  }

  isStandAlone() {
    return window.matchMedia("(display-mode: standalone)").matches;
  }

  isBR() {
    return this.currentCode && this.currentCode.toLowerCase() === "br";
  }

  closeInstall() {
    this.showInstall = false;
  }

  setIcons() {
    if (this.isIOS()) {
      const linkIcon = this.document.getElementById(
        "dynamic-192"
      ) as HTMLLinkElement | null;
      linkIcon.href = "assets/icons/android-chrome-192x192-i2.png";

      const linkIcon2 = this.document.getElementById(
        "dynamic-144"
      ) as HTMLLinkElement | null;
      linkIcon2.href = "assets/icons/android-chrome-144x144-i2.png";

      const linkIcon3 = this.document.getElementById(
        "dynamic-96"
      ) as HTMLLinkElement | null;
      linkIcon3.href = "assets/icons/android-chrome-96x96-i2.png";

      const linkIcon4 = this.document.getElementById(
        "dynamic-72"
      ) as HTMLLinkElement | null;
      linkIcon4.href = "assets/icons/android-chrome-72x72-i2.png";

      const linkIcon5 = this.document.getElementById(
        "dynamic-48"
      ) as HTMLLinkElement | null;
      linkIcon5.href = "assets/icons/favicon-48x48-i2.png";
    } else {
      const linkIcon = this.document.getElementById(
        "dynamic-192-a"
      ) as HTMLLinkElement | null;
      linkIcon.href = "assets/icons/android-chrome-192x192-i2.png";

      const linkIcon2 = this.document.getElementById(
        "dynamic-144-a"
      ) as HTMLLinkElement | null;
      linkIcon2.href = "assets/icons/android-chrome-144x144-i2.png";

      const linkIcon3 = this.document.getElementById(
        "dynamic-96-a"
      ) as HTMLLinkElement | null;
      linkIcon3.href = "assets/icons/android-chrome-96x96-i2.png";

      const linkIcon4 = this.document.getElementById(
        "dynamic-72-a"
      ) as HTMLLinkElement | null;
      linkIcon4.href = "assets/icons/android-chrome-72x72-i2.png";

      const linkIcon5 = this.document.getElementById(
        "dynamic-48-a"
      ) as HTMLLinkElement | null;
      linkIcon5.href = "assets/icons/favicon-48x48-i2.png";
    }
  }

  openBanner(banner: ImageSEO, position: number): void {
    if (this.currentCode !== "co") {
      this.analyticsSrv.trackBannerClick(banner, position, this.currentCode);
    }
    let resolvedLink = banner.link;
    if (
      resolvedLink &&
      !resolvedLink.startsWith("http") &&
      !resolvedLink.startsWith("/")
    ) {
      resolvedLink = "https://" + resolvedLink;
    }
    window.open(resolvedLink, this.isMobile ? "_self" : "_blank");
  }

  onVideoLoaded(video: HTMLVideoElement): void {
    video.muted = true;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise.catch(err => {
        console.warn("Autoplay blocked by the browser:", err);
      });
    }
  }

  openModal(): void {
    this.matDialog.open(ExclusiveContentModalComponent, {
      width: "800px",
      disableClose: false
    });
  }
}
